import { useCallback, useContext, useEffect } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import {
	SPACE_VIEWS_PAGINATE_EXPERIENCE,
	SPACE_VIEWS_CHANGE_EXPERIENCE,
	SPACE_VIEWS_INITIAL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import type { ExperienceName } from '@confluence/experience-tracker/entry-points/ExperienceNameType.ts';

import type { SpaceViewsPersistenceOption } from './__generated__/useSpaceViewsOptionsPersistenceQuery.graphql.ts';

type ExperienceTrackingParams = {
	loading: boolean;
	error: Error | undefined;
	persistenceError: ApolloErrorInterop | undefined;
	persistenceUpdateLoading: boolean;
	persistenceUpdateError: ApolloErrorInterop | undefined;
	spaceViewsSort: SpaceViewsPersistenceOption;
	setSpaceViewsSort: (newSort: SpaceViewsPersistenceOption) => void;
	isSpaceAdmin: boolean;
};

const stopOrSucceed = (
	name: ExperienceName,
	attributes: Record<string, any>,
	error: Error | undefined,
	experienceTracker: ExperienceTrackerAPI,
) => {
	if (error) {
		experienceTracker.stopOnError({ name, attributes, error });
	} else {
		experienceTracker.succeed({ name, attributes });
	}
};

export const useSpaceViewsExperienceTracking = ({
	loading,
	error,
	persistenceError,
	persistenceUpdateLoading,
	persistenceUpdateError,
	spaceViewsSort,
	setSpaceViewsSort,
	isSpaceAdmin,
}: ExperienceTrackingParams): {
	makeTrackedFetchMore: (fetchMore: any) => () => void;
	trackedSetSpaceViewsSort: (newSort: SpaceViewsPersistenceOption) => void;
} => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const makeTrackedFetchMore = useCallback(
		(fetchMore: any): (() => void) =>
			() => {
				experienceTracker.start({ name: SPACE_VIEWS_PAGINATE_EXPERIENCE });
				fetchMore();
			},
		[experienceTracker],
	);

	const trackedSetSpaceViewsSort = useCallback(
		(newSort: SpaceViewsPersistenceOption): void => {
			experienceTracker.start({
				name: SPACE_VIEWS_CHANGE_EXPERIENCE,
				attributes: { prevSort: spaceViewsSort },
			});
			setSpaceViewsSort(newSort);
		},
		[experienceTracker, spaceViewsSort, setSpaceViewsSort],
	);

	useEffect(() => {
		experienceTracker.start({ name: SPACE_VIEWS_INITIAL_EXPERIENCE });
	}, [experienceTracker]);

	useEffect(() => {
		if (!loading && !persistenceUpdateLoading) {
			const attributes = { spaceViewsSort };

			stopOrSucceed(
				SPACE_VIEWS_INITIAL_EXPERIENCE,
				{
					spaceViewsSort,
					isSpaceAdmin,
				},
				error || persistenceError,
				experienceTracker,
			);
			stopOrSucceed(
				SPACE_VIEWS_CHANGE_EXPERIENCE,
				attributes,
				error || persistenceUpdateError,
				experienceTracker,
			);
			stopOrSucceed(SPACE_VIEWS_PAGINATE_EXPERIENCE, attributes, error, experienceTracker);
		}
	}, [
		experienceTracker,
		loading,
		error,
		persistenceError,
		persistenceUpdateLoading,
		persistenceUpdateError,
		spaceViewsSort,
		isSpaceAdmin,
	]);

	return { makeTrackedFetchMore, trackedSetSpaceViewsSort };
};
